import { computed, onMounted, ref } from "vue";
import { fetchCollected, type CollectedResponse } from "@app/server/client";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { MembershipWalletPass } from "@app/server/trpc/routers/wallet-passes";
import type { CoinWalletPass } from "@app/server/trpc/routers/wallet-passes";
import { COIN_PREFIX, MEM_PREFIX, type Place } from "./placeTypes";
import { passArtworkUrl, passBrowserEnabled } from "./passDesign";

/** Loads and normalises every pass shown by the collected wallet. */
export const useCollectedWallet = () => {
  const trpc = useTrpc();
  const data = ref<CollectedResponse | null>(null);
  const memberships = ref<MembershipWalletPass[]>([]);
  const coinPasses = ref<CoinWalletPass[]>([]);
  const loading = ref(true);
  const signedOut = ref(false);
  const failed = ref(false);
  let loadPromise: Promise<void> | null = null;

  const loadMemberships = async () => {
    const [nextMemberships, nextCoins] = await Promise.allSettled([
      trpc.walletPasses.membershipPasses.query(),
      trpc.walletPasses.coinPasses.query(),
    ]);

    // A deployment mismatch or transient failure for one pass type must not
    // discard a successful response for the other. Missing passes render as
    // disabled wallet badges until a later refresh can obtain their URLs.
    if (nextMemberships.status === "fulfilled") {
      memberships.value = nextMemberships.value;
    }
    if (nextCoins.status === "fulfilled") {
      coinPasses.value = nextCoins.value;
    }
  };

  const load = (force = false) => {
    if (loadPromise && !force) return loadPromise;

    const nextLoad = (async () => {
      loading.value = data.value === null;
      signedOut.value = false;
      failed.value = false;
      try {
        const [collected] = await Promise.all([
          fetchCollected(),
          loadMemberships(),
        ]);
        data.value = collected;
      } catch (error: any) {
        if (error?.message === "401") signedOut.value = true;
        else failed.value = true;
      } finally {
        loading.value = false;
      }
    })();

    loadPromise = nextLoad;
    void nextLoad.finally(() => {
      if (loadPromise === nextLoad) loadPromise = null;
    });
    return nextLoad;
  };

  onMounted(() => void load());

  const places = computed<Place[]>(() => {
    const byOwner = new Map<string, Place>();

    for (const shop of data.value?.shops ?? []) {
      // The API normally omits shops whose programs are all disabled. Keep the
      // wallet defensive against stale/cached responses so an empty place card
      // can never survive after the last visible coin program is removed.
      if (!shop.programs?.length) continue;

      const key = `shop:${shop.restaurant_id}`;
      const place = byOwner.get(key) ?? {
        key,
        name: shop.name,
        slug: shop.slug,
        shop,
        items: [],
      };
      for (const program of shop.programs ?? []) {
        // The program's design decides whether Collected shows this card.
        if (!passBrowserEnabled("coin", program.design)) continue;
        const walletPass = coinPasses.value.find(
          (pass) => pass.program_id === program.coin_program_id,
        );
        place.items.push({
          kind: "coin",
          key: COIN_PREFIX + program.coin_program_id,
          shop,
          program,
          walletPass,
        });
      }
      if (place.items.length > 0) byOwner.set(key, place);
    }

    for (const pass of memberships.value) {
      if (!passBrowserEnabled("membership", pass.design)) continue;
      const key = pass.shop_id
        ? `shop:${pass.shop_id}`
        : pass.shop_group_id
          ? `group:${pass.shop_group_id}`
          : `memprog:${pass.program_id}`;
      const place = byOwner.get(key) ?? {
        key,
        name: pass.place_name,
        slug: pass.place_slug,
        shop: null,
        items: [],
      };
      place.items.push({
        kind: "membership",
        key: MEM_PREFIX + pass.program_id,
        pass,
      });
      byOwner.set(key, place);
    }

    return [...byOwner.values()];
  });

  /** Each pass's own design artwork — never another program's. */
  const passImages = computed<Record<string, string>>(() => {
    const images: Record<string, string> = {};
    for (const place of places.value) {
      for (const item of place.items) {
        const url =
          item.kind === "coin"
            ? passArtworkUrl("coin", item.program.design)
            : passArtworkUrl("membership", item.pass.design);
        if (url) images[item.key] = url;
      }
    }
    return images;
  });

  const imageForPass = (passKey: string) => passImages.value[passKey] ?? null;

  return {
    failed,
    imageForPass,
    load,
    loading,
    places,
    passImages,
    signedOut,
    totals: computed(() => data.value?.totals),
  };
};
